import React, { useState } from 'react';
import { useGame } from '../../context/GameContext';
import { resources, buildings, technologies, BALANCE } from '../../data';
import { ResourceChip } from '../common';

function SpecializationModal({ buildingId, onClose, onNotification }) {
  const {
    playerResources,
    buildingSpecializations,
    researchedTechs,
    specializeBuilding,
    getSpecializationEffect,
  } = useGame();

  const building = buildings[buildingId];
  if (!building) return null;

  const currentSpecId = buildingSpecializations[buildingId];
  const currentSpec = currentSpecId ? getSpecializationEffect(buildingId) : null;
  const currentTier = currentSpec?.tier || 0;
  const allSpecs = BALANCE.specializations[buildingId] || [];
  
  // Default to next available tier tab
  const [selectedTier, setSelectedTier] = useState(currentTier + 1 || 1);
  
  // Group specs by tier
  const specsByTier = {
    1: allSpecs.filter(s => s.tier === 1),
    2: allSpecs.filter(s => s.tier === 2),
    3: allSpecs.filter(s => s.tier === 3),
  };

  // Check if tech is researched
  const hasTech = (techId) => {
    if (!techId) return true;
    return researchedTechs.includes(techId);
  };

  // Check if can afford
  const canAfford = (spec) => {
    return Object.entries(spec.cost).every(
      ([r, a]) => (playerResources[r] || 0) >= a
    );
  };

  // Get tech name
  const getTechName = (techId) => {
    const tech = technologies[techId];
    return tech ? `${tech.icon} ${tech.name}` : techId;
  };

  // Get tier info
  const getTierInfo = (tier) => {
    switch(tier) {
      case 1: return { name: 'Basic', icon: '🔧', color: '#6b8e23' };
      case 2: return { name: 'Advanced', icon: '⚙️', color: '#d4a574' };
      case 3: return { name: 'Master', icon: '✨', color: '#a855f7' };
      default: return { name: 'Unknown', icon: '?', color: '#666' };
    }
  };

  const handleSpecialize = (specId) => {
    const spec = allSpecs.find(s => s.id === specId);
    if (specializeBuilding(buildingId, specId)) {
      const action = currentSpecId ? 'upgraded to' : 'specialized to';
      onNotification(`${building.name} ${action} ${spec.name}!`, 'success');
      onClose();
    }
  };

  // Get available tiers for selection (higher than current)
  const availableTiers = [1, 2, 3].filter(t => specsByTier[t]?.length > 0 && t > currentTier);
  const isMaxTier = currentTier >= 3;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="spec-modal-v2" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="spec-header-v2">
          <div className="spec-header-left">
            <span className="spec-building-icon">{building.icon}</span>
            <div className="spec-header-titles">
              <h2>{currentSpecId ? 'Upgrade' : 'Specialize'} {building.name}</h2>
              <span className="spec-header-sub">
                {currentSpecId ? 'Upgrade to a higher tier' : 'Choose a permanent upgrade path'}
              </span>
            </div>
          </div>
          <button className="spec-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="spec-content-v2">
          {/* Current Spec Display (if specialized) */}
          {currentSpec && (
            <div className="spec-current-box">
              <div className="spec-current-label">Current Specialization</div>
              <div className="spec-current-card">
                <span className="spec-current-icon">{currentSpec.icon}</span>
                <div className="spec-current-info">
                  <div className="spec-current-name">{currentSpec.name}</div>
                  <div className="spec-current-tier" style={{ color: getTierInfo(currentTier).color }}>
                    {getTierInfo(currentTier).icon} {getTierInfo(currentTier).name} Tier
                  </div>
                </div>
                <div className="spec-current-effect">{currentSpec.effect}</div>
              </div>
            </div>
          )}

          {isMaxTier ? (
            /* Max tier reached */
            <div className="spec-max-tier">
              <span className="spec-max-icon">👑</span>
              <div className="spec-max-text">Maximum tier reached!</div>
              <p className="spec-max-sub">This building has the highest specialization available.</p>
            </div>
          ) : (
            <>
              {/* Tier Tabs */}
              {availableTiers.length > 1 && (
                <div className="spec-tier-tabs">
                  {availableTiers.map(tier => {
                    const info = getTierInfo(tier);
                    const specs = specsByTier[tier];
                    const anyUnlocked = specs.some(s => hasTech(s.techRequired));
                    
                    return (
                      <button
                        key={tier}
                        className={`spec-tier-tab ${selectedTier === tier ? 'active' : ''} ${!anyUnlocked ? 'locked' : ''}`}
                        onClick={() => setSelectedTier(tier)}
                        style={{ '--tier-color': info.color }}
                      >
                        <span className="tier-icon">{info.icon}</span>
                        <span className="tier-name">{info.name}</span>
                        {!anyUnlocked && <span className="tier-lock">🔒</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Single tier header when only one available */}
              {availableTiers.length === 1 && (
                <div className="spec-single-tier-header" style={{ '--tier-color': getTierInfo(availableTiers[0]).color }}>
                  <span>{getTierInfo(availableTiers[0]).icon}</span>
                  <span>{currentSpecId ? 'Upgrade to' : 'Choose'} {getTierInfo(availableTiers[0]).name} Tier</span>
                </div>
              )}

              {/* Info Box */}
              <div className="spec-info-box">
                <div className="spec-info-title">How Specializations Work</div>
                <div className="spec-info-row">
                  <span className="spec-info-icon">🔧</span>
                  <span>Choose a <strong>Basic</strong> tier specialization to start</span>
                </div>
                <div className="spec-info-row">
                  <span className="spec-info-icon">⬆️</span>
                  <span>Later, upgrade to <strong>Advanced</strong> and <strong>Master</strong> tiers</span>
                </div>
                <div className="spec-info-row">
                  <span className="spec-info-icon">🔄</span>
                  <span>Upgrading <strong>replaces</strong> your current spec (no refunds)</span>
                </div>
                <div className="spec-info-row">
                  <span className="spec-info-icon">🔬</span>
                  <span>Higher tiers require specific <strong>technologies</strong></span>
                </div>
                <div className="spec-info-row warning">
                  <span className="spec-info-icon">⚠️</span>
                  <span>You <strong>cannot</strong> switch between specs of the same tier</span>
                </div>
              </div>

              {/* Spec Grid */}
              <div className="spec-grid-v2">
                {specsByTier[selectedTier]?.map(spec => {
                  const techUnlocked = hasTech(spec.techRequired);
                  const affordable = canAfford(spec);
                  const canChoose = techUnlocked && affordable;
                  const tierInfo = getTierInfo(spec.tier);

                  return (
                    <div 
                      key={spec.id} 
                      className={`spec-card-v2 ${canChoose ? 'available' : ''} ${!techUnlocked ? 'tech-locked' : ''} ${!affordable && techUnlocked ? 'unaffordable' : ''}`}
                      style={{ '--tier-color': tierInfo.color }}
                    >
                      {/* Card Header */}
                      <div className="spec-card-header">
                        <span className="spec-card-icon">{spec.icon}</span>
                        <div className="spec-card-tier-badge">T{spec.tier}</div>
                      </div>

                      {/* Card Body */}
                      <div className="spec-card-body">
                        <div className="spec-card-name">{spec.name}</div>
                        <div className="spec-card-desc">{spec.description}</div>
                        <div className="spec-card-effect">{spec.effect}</div>
                      </div>

                      {/* Tech Requirement */}
                      {spec.techRequired && (
                        <div className={`spec-card-tech ${techUnlocked ? 'unlocked' : 'locked'}`}>
                          <span className="tech-label">Requires:</span>
                          <span className="tech-name">{getTechName(spec.techRequired)}</span>
                          {techUnlocked ? <span className="tech-check">✓</span> : <span className="tech-lock">🔒</span>}
                        </div>
                      )}

                      {/* Cost */}
                      <div className="spec-card-cost">
                        {Object.entries(spec.cost).map(([r, a]) => (
                          <ResourceChip
                            key={r}
                            resourceId={r}
                            amount={a}
                            currentAmount={playerResources[r] || 0}
                            canAfford={(playerResources[r] || 0) >= a}
                            size="small"
                            showTooltip={false}
                          />
                        ))}
                      </div>

                      {/* Action Button */}
                      <button 
                        className="spec-card-btn"
                        disabled={!canChoose}
                        onClick={() => handleSpecialize(spec.id)}
                      >
                        {!techUnlocked ? '🔒 Research Required' : !affordable ? '💰 Cannot Afford' : currentSpecId ? '⬆️ Upgrade' : '✨ Specialize'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SpecializationModal;
